import * as THREE from 'three'

/**
 * Procedural cockpit surfaces.
 *
 * Untextured boxes read as flat silhouettes no matter what colour they are —
 * which is most of why the first few cockpits looked like cardboard. Panel
 * lines, screws, wear and labels are what make a surface look manufactured,
 * and a canvas produces all of them for a few kilobytes.
 *
 * This will not pass for a photogrammetry scan. It is aiming for "clearly a
 * machine someone built", which is achievable, rather than "photoreal", which
 * is not without real assets.
 */

/** Deterministic pseudo-random, so the cockpit looks the same every load. */
function makeRng(seed = 20250803) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 4294967296
  }
}

function screw(ctx, x, y, r, rnd) {
  ctx.fillStyle = 'rgba(20,22,24,0.75)'
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.strokeStyle = 'rgba(190,196,200,0.45)'
  ctx.lineWidth = Math.max(1, r * 0.35)
  const a = rnd() * Math.PI
  ctx.beginPath()
  ctx.moveTo(x - Math.cos(a) * r * 0.8, y - Math.sin(a) * r * 0.8)
  ctx.lineTo(x + Math.cos(a) * r * 0.8, y + Math.sin(a) * r * 0.8)
  ctx.stroke()
}

function grime(ctx, w, h, rnd, amount = 0.06) {
  for (let i = 0; i < 2600; i++) {
    const v = rnd()
    ctx.fillStyle =
      v > 0.5 ? `rgba(255,255,255,${amount * 0.4})` : `rgba(0,0,0,${amount})`
    ctx.fillRect(rnd() * w, rnd() * h, 1 + rnd() * 2, 1 + rnd() * 2)
  }
}

function finish(canvas) {
  const t = new THREE.CanvasTexture(canvas)
  t.colorSpace = THREE.SRGBColorSpace
  t.anisotropy = 8
  return t
}

/**
 * Instrument panel face: grey with instrument cut-outs, screw rows, warning
 * placards and a switch strip.
 */
export function panelTexture() {
  const W = 1024
  const H = 640
  const c = document.createElement('canvas')
  c.width = W
  c.height = H
  const ctx = c.getContext('2d')
  const rnd = makeRng(11)

  // Base with a vertical gradient — panels are lit from the canopy above.
  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, '#7c8288')
  g.addColorStop(0.45, '#6b7176')
  g.addColorStop(1, '#565c61')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  // Sub-panel divisions.
  ctx.strokeStyle = 'rgba(28,32,35,0.55)'
  ctx.lineWidth = 3
  for (const x of [0.24, 0.5, 0.76]) {
    ctx.beginPath()
    ctx.moveTo(x * W, 0)
    ctx.lineTo(x * W, H)
    ctx.stroke()
  }
  ctx.beginPath()
  ctx.moveTo(0, H * 0.52)
  ctx.lineTo(W, H * 0.52)
  ctx.stroke()

  // Screws along every seam.
  for (const x of [0.24, 0.5, 0.76]) {
    for (let i = 0; i < 12; i++) screw(ctx, x * W, (i + 0.5) * (H / 12), 4, rnd)
  }
  for (let i = 0; i < 18; i++) screw(ctx, (i + 0.5) * (W / 18), H * 0.52, 4, rnd)
  for (let i = 0; i < 18; i++) {
    screw(ctx, (i + 0.5) * (W / 18), 10, 4, rnd)
    screw(ctx, (i + 0.5) * (W / 18), H - 10, 4, rnd)
  }

  // Placards.
  ctx.font = '600 13px ui-monospace, monospace'
  ctx.textAlign = 'center'
  const labels = [
    ['AOA', 0.13, 0.1],
    ['ADI', 0.5, 0.1],
    ['ALT', 0.87, 0.1],
    ['IAS', 0.13, 0.62],
    ['HSI', 0.5, 0.62],
    ['VVI', 0.87, 0.62],
  ]
  for (const [text, u, v] of labels) {
    ctx.fillStyle = 'rgba(20,22,24,0.7)'
    ctx.fillText(text, u * W, v * H)
  }

  // A warning placard, because every panel has one.
  ctx.fillStyle = '#8d2a22'
  ctx.fillRect(W * 0.42, H * 0.86, W * 0.16, H * 0.07)
  ctx.fillStyle = '#e8e2d8'
  ctx.font = '700 12px ui-monospace, monospace'
  ctx.fillText('MASTER CAUTION', W * 0.5, H * 0.905)

  grime(ctx, W, H, rnd, 0.07)
  return finish(c)
}

/** Dark structural surfaces: coaming, consoles, canopy rails. */
export function structureTexture() {
  const W = 512
  const H = 512
  const c = document.createElement('canvas')
  c.width = W
  c.height = H
  const ctx = c.getContext('2d')
  const rnd = makeRng(7)

  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, '#3a4149')
  g.addColorStop(1, '#232a31')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, H)

  // Non-slip / textured finish.
  for (let i = 0; i < 9000; i++) {
    ctx.fillStyle = rnd() > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.06)'
    ctx.fillRect(rnd() * W, rnd() * H, 2, 2)
  }

  // Rivet lines along the edges.
  for (let i = 0; i < 16; i++) {
    screw(ctx, (i + 0.5) * (W / 16), 14, 3.5, rnd)
    screw(ctx, (i + 0.5) * (W / 16), H - 14, 3.5, rnd)
  }

  grime(ctx, W, H, rnd, 0.05)
  return finish(c)
}
